import { useEffect, useState } from 'react';

import { type UnconnectedApp } from '@atlassian/conversation-assistant-chat-prompt-input/app-connection-banner';
import { useAppConnectionBannerStore } from '@atlassian/conversation-assistant-chat-prompt-input/useAppConnectionBannerStore';

/**
 * Latches the 3P suggested-prompts connection prompt's apps per conversation
 * (mirrors the `pinnedRecommendationConnector` latch in chat-history).
 *
 * The apps are seeded into the transient app-connection-banner store by the 3P
 * flow. Pinning a copy here keeps the in-thread prompt mounted across new
 * messages even after that store is cleared on submit, and scopes it to the
 * conversation it was pinned for so it can't render in a different one.
 *
 * Returns the pinned apps for `conversationId`, or `null` when there is no
 * prompt to show for it.
 */
export const usePinnedAppConnectionPrompt = (conversationId: string): UnconnectedApp[] | null => {
	const [{ unconnectedApps, showConnectedState }] = useAppConnectionBannerStore();
	const [pinned, setPinned] = useState<{
		conversationId: string;
		apps: UnconnectedApp[];
	} | null>(null);

	useEffect(() => {
		if (!showConnectedState || unconnectedApps.length === 0) {
			return;
		}
		setPinned((previous) =>
			previous?.conversationId === conversationId
				? previous
				: { conversationId, apps: unconnectedApps },
		);
	}, [conversationId, showConnectedState, unconnectedApps]);

	return pinned?.conversationId === conversationId ? pinned.apps : null;
};
